import React, { useState } from 'react';
import { Check, Pencil } from 'lucide-react';
import * as profileApi from '../../api/profile';

/**
 * Edit-in-place display name.
 *
 * This is the only name players outside your own leagues ever see, so it gets a
 * line of explanation rather than sitting there as an unexplained field - people
 * given a generated handle need to know why it is worth changing.
 */
function DisplayNameField({ value, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    setBusy(true);
    try {
      const result = await profileApi.updateDisplayName(draft);
      setEditing(false);
      if (onChanged) onChanged(result.displayName);
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.body?.error || 'Could not save that name');
      } else {
        setError('Could not save that name');
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setDraft(value || '');
    setError('');
    setEditing(false);
  };

  return (
    <div className="profile-field display-name-field">
      <span className="field-label">Display Name:</span>

      {editing ? (
        <span className="display-name-edit">
          <input
            type="text"
            value={draft}
            maxLength={30}
            aria-label="Display name"
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
          />
          <button className="primary-btn" onClick={save} disabled={busy}>
            <Check size={16} />
            Save
          </button>
          <button className="secondary-btn" onClick={cancel} disabled={busy}>
            Cancel
          </button>
        </span>
      ) : (
        <span className="display-name-view">
          <span className="field-value">{value}</span>
          <button
            className="copy-btn"
            onClick={() => setEditing(true)}
            title="Change display name"
            aria-label="Change display name"
          >
            <Pencil size={14} />
          </button>
        </span>
      )}

      {error && <span className="error-text">{error}</span>}

      <span className="helper-text">
        The only name players outside your leagues see — on league tables, join
        requests and invites. Your real name is never shown to them.
      </span>
    </div>
  );
}

export default DisplayNameField;
